import React, { useEffect, useState } from "react";
import Logo from "../../assets/images/DocuVerusLogoorg.png";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import InputField2 from "../../componets/common/InputComponents/InputField2";
import { useCreate, useNotify } from "react-admin";
import { CircularProgress } from "@mui/material";
import { SOFTWARE_VERSION, typesofUsers } from "../../config";

type OtpChannel = "email" | "sms";
type MfaMode = "legacy" | "session";

type SendOtpResult = {
  message?: string;
  successMessage?: string;
};

type MfaLoginProps = {
  mode?: MfaMode;
  username?: string;
  userId?: string;
  channels?: OtpChannel[];
  onSendOtp?: (delivery: OtpChannel) => Promise<SendOtpResult | void>;
  onVerifyOtp?: (otp: string | number) => Promise<void>;
  onBackToLogin?: () => void;
  onUsePasswordInstead?: () => void;
  showUsePasswordInstead?: boolean;
};

const notifyOptions = {
  anchorOrigin: { vertical: "top", horizontal: "right" } as const,
};

const userTypes = {
  subscribers: [
    typesofUsers?.subscriberUser,
    typesofUsers?.subscriberAdmin,
    typesofUsers?.agent,
    typesofUsers?.manager,
    typesofUsers?.admin,
  ],
  admins: [
    typesofUsers?.docuverusSuperAdmin,
    typesofUsers?.superAdmin,
    typesofUsers?.docuverusAdmin,
  ],
};

const defaultFormValues = {
  otp: "",
};

const schema = yup.object().shape({
  otp: yup
    .number()
    .typeError("Verification  Code is required!")
    .required("Verification  Code is required!"),
});

const defaultOtpMessage = (delivery: OtpChannel) =>
  delivery === "sms"
    ? "Verification code sent via text message. Carrier fees may apply."
    : "Verification code sent via email.";

const maskOtpContactDetails = (message: string) =>
  String(message || "")
    .replace(
      /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
      (emailAddress) => emailAddress.replace(/(^.{3}).*(@.*$)/, "$1***$2")
    )
    .replace(/\+?\d[\d\s().-]{5,}\d/g, (phoneNumber) => {
      const digits = phoneNumber.replace(/\D/g, "");
      if (digits.length <= 4) {
        return digits;
      }

      const countryCode = phoneNumber.trim().startsWith("+") && digits.length > 10
        ? `+${digits.slice(0, digits.length - 10)} `
        : "";
      const localNumber = digits.slice(-10);
      return `${countryCode}${localNumber.slice(0, 3)} ***${localNumber.slice(-4)}`;
    });

const normalizeChannels = (channels: OtpChannel[] = []) => {
  const availableChannels = channels.filter(
    (channel): channel is OtpChannel => channel === "email" || channel === "sms"
  );

  if (availableChannels.length > 0) {
    return Array.from(new Set(availableChannels));
  }

  return ["sms", "email"] as OtpChannel[];
};

const MfaLogin = ({
  mode = "legacy",
  username = "",
  userId,
  channels = [],
  onSendOtp,
  onVerifyOtp,
  onBackToLogin,
  onUsePasswordInstead,
  showUsePasswordInstead = false,
}: MfaLoginProps) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState("");
  const [sentChannels, setSentChannels] = useState<OtpChannel[]>([]);
  const [create, legacyCreateState] = useCreate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const availableChannels = normalizeChannels(channels);
  const isSessionMode = mode === "session";

  const methods = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  useEffect(() => {
    document.title = "Docuverus";
  }, []);

  const handleSendError = (message: string) => {
    notify(message, {
      type: "error",
      ...notifyOptions,
    });
  };

  const handleLegacySendOtp = (delivery: OtpChannel) => {
    const resource =
      delivery === "sms" ? "mfa_sendmfasmsotp" : "mfa_SendMfaEmailOtp";

    create(
      resource,
      { data: { username, ...(userId ? { UserID: userId } : {}) } },
      {
        onSuccess: (data: any) => {
          const detail =
            delivery === "sms" ? data?.records?.sms : data?.records?.email;
          setStatusMessage(maskOtpContactDetails(detail || defaultOtpMessage(delivery)));
          setSentChannels((currentChannels) =>
            currentChannels.includes(delivery)
              ? currentChannels
              : [...currentChannels, delivery]
          );
          notify(`${data?.message}`, {
            type: "success",
            ...notifyOptions,
          });
        },
        onError: (error: any) => {
          handleSendError(error?.message || "Unable to send verification code.");
        },
      }
    );
  };

  const handleSessionSendOtp = async (delivery: OtpChannel) => {
    if (!onSendOtp) return;

    setIsSubmitting(true);
    try {
      const result = await onSendOtp(delivery);
      setStatusMessage(
        maskOtpContactDetails(result?.message || defaultOtpMessage(delivery))
      );
      setSentChannels((currentChannels) =>
        currentChannels.includes(delivery)
          ? currentChannels
          : [...currentChannels, delivery]
      );
      notify(result?.successMessage || "Verification code sent successfully.", {
        type: "success",
        ...notifyOptions,
      });
    } catch (error: any) {
      handleSendError(error?.message || "Unable to send verification code.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendOtp = (delivery: OtpChannel) => {
    if (isSessionMode) {
      void handleSessionSendOtp(delivery);
      return;
    }

    handleLegacySendOtp(delivery);
  };

  const onSubmit = async (data: typeof defaultFormValues) => {
    if (isSessionMode) {
      if (!onVerifyOtp) return;

      setIsSubmitting(true);
      try {
        await onVerifyOtp(data?.otp);
        methods.reset(defaultFormValues);
      } catch (error: any) {
        notify(error?.message || "The verification code could not be verified.", {
          type: "error",
          ...notifyOptions,
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    create(
      "mfa_VerifyOtp",
      {
        data: {
          username,
          ...(userId ? { UserID: userId } : {}),
          otp: data?.otp,
        },
      },
      {
        onSuccess: (response: any) => {
          if (userTypes.subscribers.includes(response?.records?.userrole)) {
            localStorage.setItem(
              "auth",
              JSON.stringify({
                ...response.records,
                data: { ...response.records, subscriberView: true },
              })
            );
          }

          if (userTypes.admins.includes(response?.records?.userrole)) {
            localStorage.setItem(
              "auth",
              JSON.stringify({
                ...response.records,
                data: { ...response.records, subscriberView: false },
              })
            );
          }

          notify(`${response?.message}`, {
            type: "success",
            ...notifyOptions,
          });
          methods.reset(defaultFormValues);
          navigate("/ATS", { replace: true });
        },
        onError: (error: any) => {
          notify(error?.message || "The verification code could not be verified.", {
            type: "error",
            ...notifyOptions,
          });
        },
      }
    );
  };

  const isLoading = isSessionMode
    ? isSubmitting
    : Boolean(legacyCreateState?.isLoading);

  return (
    <section className="login-wrap">
      <div className="login-cell">
        <div className="login-block">
          <div className="l-logo-block text-center">
            <img src={Logo} alt="Docuverus" title="Docuverus" />
          </div>
          <br />
          <p
            style={{
              color: "green",
              textAlign: "center",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {statusMessage}
          </p>
          <div className="d-flex justify-content-center align-items-center">
            {availableChannels.includes("sms") ? (
              <button
                type="button"
                style={{ fontSize: "14px" }}
                onClick={() => sendOtp("sms")}
                className="btn btn-link"
                disabled={isLoading}
              >
                {sentChannels.includes("sms")
                  ? "Resend Verification Code via SMS"
                  : "Send Verification Code via SMS"}
              </button>
            ) : null}
            {availableChannels.includes("email") ? (
              <button
                type="button"
                style={{ fontSize: "14px" }}
                onClick={() => sendOtp("email")}
                className="btn btn-link"
                disabled={isLoading}
              >
                {sentChannels.includes("email")
                  ? "Resend Verification Code via Email"
                  : "Send Verification Code via Email"}
              </button>
            ) : null}
          </div>

          <div className="auth-form mr-t30">
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="form-group cus-input-group mr-b20">
                  <InputField2
                    label="Verification Code"
                    source="otp"
                    type="password"
                    placeholder="Enter Verification Code"
                    labelClass="lable-title"
                    additionalInputClass="cus-control"
                  />
                </div>

                {isLoading ? (
                  <div className="static-d-info d-flex justify-content-center align-items-center mw-100">
                    <CircularProgress size="20px" />
                    <h5>&nbsp; &nbsp; Please wait...</h5>
                  </div>
                ) : (
                  <div className="d-flex justify-content-center align-items-center">
                    <button type="submit" className="l-btn-forgetPsd">
                      Verify Code
                    </button>
                  </div>
                )}
              </form>
              <br />
              <div className="w-full p-t-3 p-b-5 text-right">
                {showUsePasswordInstead && onUsePasswordInstead ? (
                  <button
                    type="button"
                    className="btn btn-link txt1"
                    onClick={onUsePasswordInstead}
                  >
                    Use password instead
                  </button>
                ) : null}
                {onBackToLogin ? (
                  <button
                    type="button"
                    className="btn btn-link txt1"
                    style={{ color: "#5555FF" }}
                    onClick={onBackToLogin}
                  >
                    Back to Login
                  </button>
                ) : (
                  <Link to="/" className="txt1" style={{ color: "#5555FF" }}>
                    Back to Login
                  </Link>
                )}
              </div>
            </FormProvider>
          </div>

          <div className="l-foot-links mr-t20">
            <ul className="mx-auto">
              <li>
                <a href="/atss/DataPolicy" target="_blank" rel="noreferrer">
                  Data Policy
                </a>
              </li>
              <li>
                <a href="/atss/TermsOfUse" target="_blank" rel="noreferrer">
                  Terms of Use
                </a>
              </li>
              <li>Version {SOFTWARE_VERSION}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MfaLogin;
